import { useEffect, useState } from "react";
import { Box, Grid, Stack } from "@mui/material";
import { DashboardKey, ActiveDashboardWidget,
  ALL_DASHBOARD_WIDGETS,
  getDashboardStorageKey,
  getDefaultWidgetsForDashboard,
  WidgetSlot, } from "./DashboardWidgetsConfig";

interface DashboardWidgetRendererProps {
  dashboardKey: DashboardKey;
  widgetProps?: Record<string, unknown>;
}

export default function DashboardWidgetRenderer({
  dashboardKey,
  widgetProps = {},
}: DashboardWidgetRendererProps) {
  const [activeWidgets, setActiveWidgets] = useState<ActiveDashboardWidget[]>([]);

  useEffect(() => {
    const storageKey = getDashboardStorageKey(dashboardKey);
    const saved = localStorage.getItem(storageKey);
    setActiveWidgets(
      saved ? JSON.parse(saved) : getDefaultWidgetsForDashboard(dashboardKey)
    );
  }, [dashboardKey]);

  const visible = activeWidgets.filter((w) => w.visible);

  const getWidget = (slotType: WidgetSlot["type"]) =>
    visible.find((w) => w.slot.type === slotType);

  const getWidgetNode = (slotType: WidgetSlot["type"]) => {
    const active = getWidget(slotType);
    if (!active) return null;
    const info = ALL_DASHBOARD_WIDGETS.find((w) => w.id === active.id);
    return info?.render(widgetProps) ?? null;
  };

  const mainGridWidgets = visible.filter((w) => w.slot.type === "main_grid");

  return (
    <Box sx={{ px: 3, mt: 3 }}>
      <Grid container spacing={3}>

        <Grid size={{ xs: 12, md: 3 }}>
          <Stack spacing={3}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
            }}>
              <Box sx={{ aspectRatio: "1 / 1" }}>
                {getWidgetNode("sidebar_top_left")}
              </Box>
              <Box sx={{ aspectRatio: "1 / 1" }}>
                {getWidgetNode("sidebar_top_right")}
              </Box>
            </Box>

            {getWidgetNode("sidebar_bottom")}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Stack spacing={3}>
            {getWidgetNode("main_primary")}

            <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
              {mainGridWidgets.map((activeWidget) => {
                const info = ALL_DASHBOARD_WIDGETS.find(
                  (w) => w.id === activeWidget.id
                );
                if (!info) return null;
                const slot = activeWidget.slot as { type: "main_grid"; size: { xs: number; md: number } };

                return (
                  <Grid key={activeWidget.id} size={{ xs: slot.size.xs, md: slot.size.md }}>
                    <Box sx={{ height: { xs: 480, md: 620 }, overflow: "hidden" }}>
                      {info.render(widgetProps)}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Grid>

      </Grid>
    </Box>
  );
}